import React, { memo, useRef } from 'react';
import type { OptionsItem as OptionsItemType, OptionsListStyle, ItemLayout } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { useElementAnimation, useSelectionAnimation, mergeAnimationClassName } from '../../../animations';
import {
  ImageTitle,
  TitleImage,
  ImageTitleSubtitle,
  TitleSubtitleImage,
  TitleSubtitle,
  TitleOnly,
  TitleCheckbox,
  ImageTitleCheckbox,
  TitleSubtitleCheckbox,
  ImageTitleSubtitleCheckbox,
  CheckboxTitle,
  CheckboxTitleImage,
  CheckboxTitleSubtitle,
  CheckboxTitleSubtitleImage,
  GridCard,
} from './variants';
import type { OptionsItemVariantProps } from './variants';
import { getSchemaTestId } from '../../../utils/testIdUtils';

export interface OptionsItemRouterProps {
  item: OptionsItemType | undefined;
  itemLayout: ItemLayout | undefined;
  index: number;
  locale: string;
  variables?: TemplateVariables;
  selectable: boolean;
  selected: boolean;
  disabled?: boolean;
  appearance?: OptionsListStyle;
  selectedAppearance?: OptionsListStyle;
  onSelect?: (item: OptionsItemType, index: number, selected: boolean) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
  cellHeightFromCellWidth?: number;
  columnLayout?: string;
  /** `OptionsList.selectionMode` — drives the option's ARIA role (D-T9b-14). */
  selectionMode?: string;
  /** 0-based display position (post-shuffle) — feeds itemStagger math */
  staggerIndex?: number;
}

type VariantComponent = React.ComponentType<OptionsItemVariantProps>;

const VARIANT_MAP: Record<string, VariantComponent> = {
  ImageTitle,
  TitleImage,
  ImageTitleSubtitle,
  TitleSubtitleImage,
  TitleSubtitle,
  Title: TitleOnly,
  TitleCheckbox,
  ImageTitleCheckbox,
  TitleSubtitleCheckbox,
  ImageTitleSubtitleCheckbox,
  CheckboxTitle,
  CheckboxTitleImage,
  CheckboxTitleSubtitle,
  CheckboxTitleSubtitleImage,
  SmallImageTitle: GridCard,
  MediumImageTitle: GridCard,
  BigImageTitle: GridCard,
  FullImage: GridCard,
};

export const OptionsItem = memo(function OptionsItem({
  item,
  itemLayout,
  index,
  locale,
  variables,
  selectable,
  selected,
  disabled,
  appearance,
  selectedAppearance,
  onSelect,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
  cellHeightFromCellWidth,
  columnLayout,
  selectionMode,
  staggerIndex,
}: OptionsItemRouterProps): React.ReactElement | null {
  const entranceAnim = useElementAnimation(editPath, { staggerIndex });
  const selectionAnim = useSelectionAnimation(editPath, selected);
  // Selection feedback replaces the entrance class while active — two
  // sv2-anim-* classes on one element would fight over animation-name.
  // Once selection feedback has shown, never fall back to the entrance
  // class: re-adding it after a deselect (with no deselectionAnimation
  // configured) restarts the CSS entrance animation. Editor Replay resets
  // this via the Fragment-key remount in ScreenAnimationProvider.
  const selectionSeenRef = useRef(false);
  if (selectionAnim.className) selectionSeenRef.current = true;
  const itemAnim = selectionAnim.className
    ? selectionAnim
    : (selectionSeenRef.current ? {} : entranceAnim);

  if (!item) {
    return null;
  }

  const Variant = (itemLayout && VARIANT_MAP[itemLayout]) || ImageTitleSubtitle;

  // The option ROOT carried no data-testid — only its children (`…items.0.title`,
  // `…items.0.image`) did, which is why the T9b keyboard walk could not address an option
  // at all. Derive it from the edit path exactly like every other schema-driven testid.
  const resolvedTestId = testId ?? getSchemaTestId(editPath);

  // Insets (item padding) are not a per-state property: the editor exposes a single
  // "Item paddings" control bound to appearance.insets, while templates may carry
  // stale selectedAppearance.insets. Selecting an item must never change its padding.
  const effectiveSelectedAppearance = selectedAppearance
    ? { ...selectedAppearance, insets: appearance?.insets }
    : selectedAppearance;

  return (
    <Variant
      item={item}
      index={index}
      locale={locale}
      variables={variables}
      selectable={selectable}
      selected={selected}
      disabled={disabled}
      appearance={appearance}
      selectedAppearance={effectiveSelectedAppearance}
      onSelect={onSelect}
      className={mergeAnimationClassName(className, itemAnim)}
      style={itemAnim.style}
      data-testid={resolvedTestId}
      editPath={editPath}
      previewMode={previewMode}
      cellHeightFromCellWidth={cellHeightFromCellWidth}
      itemLayout={itemLayout}
      columnLayout={columnLayout}
      selectionMode={selectionMode}
    />
  );
});

OptionsItem.displayName = 'OptionsItem';
